import { CommonModule, DatePipe, Location } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    NgZone,
    OnInit,
    Signal,
    signal,
    ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { debounceTime, merge, take } from 'rxjs';
import * as XLSX from 'xlsx';
import { Contact } from '../../../models/contact';
import { GovernanceType, MemberContactType, ValidityStatus } from '../../../models/enums';
import { ContactService } from '../../../services/contact.service';
import { EventService } from '../../../services/event.service';
import { InitiativeService } from '../../../services/initiative.service';
import { OrganisationService } from '../../../services/organisation.service';
import { ProjectService } from '../../../services/project.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { TableStateService } from '../../../services/table-state.service';
import { iso3ToEmojiFlag } from '../../../utils/country-utils';
import { getGridTemplate, mapObject, MY_DATE_FORMATS } from '../../../utils/utils';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import {
    AdvancedSelectComponent,
    AdvancedSelectValue,
    createAdvancedSelectValue
} from '../../misc/advanced-select/advanced-select.component';
import { ColumnSelectorDialogComponent } from '../../misc/column-selector-dialog/column-selector-dialog.component';
import { mapToNestedOptions, AttributeIconPair } from '../../misc/nested-options';
import { headerStyle } from '../../../utils/export-utils';
import { ExportService } from '../../../services/export.service';
import { DateTime } from 'luxon';
import { Organisation } from '../../../models/organisation';


interface ContactFilters {
    textFilter: string;
    topics: AdvancedSelectValue;
    stakeholderGroups: AdvancedSelectValue;
    projects: AdvancedSelectValue;
    initiatives: AdvancedSelectValue;
    events: AdvancedSelectValue;
    organisations: AdvancedSelectValue;
    contactTypes: AdvancedSelectValue;
    countries: AdvancedSelectValue;
    governance: AdvancedSelectValue;
    communicationChoices: AdvancedSelectValue;
    showActiveItems: boolean,
    showArchivedItems: boolean,
}

// map each governancetype to a boolean function

const governanceChecks: Record<GovernanceType, (c: Contact) => boolean> = {
    [GovernanceType.BOD]: c => c.boardOfDirectors,
    [GovernanceType.SAB]: c => c.scientificAdvisoryBoard,
    [GovernanceType.GA]: c => c.generalAssembly,
    [GovernanceType.EXECUTIVE]: c => c.executiveCommittee,
    [GovernanceType.NOMINATION]: c => c.nominationCommittee,
    [GovernanceType.ETHICS]: c => c.ethicsCommittee
};



@Component({
    selector: 'app-contacts',
    imports: [
        AdvancedSelectComponent,
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatChipsModule,
        MatDatepickerModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatIconModule,
        MatIconModule,
        MatInputModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
        MatSortModule,
        MatTableModule,
        MatToolbarModule,
        MatTooltipModule,
        ReactiveFormsModule,
        DatePipe,
        MatCheckboxModule
    ],
    providers: [
        provideLuxonDateAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    ],
    templateUrl: './contacts.component.html',
    styleUrl: './contacts.component.scss'
})
export class ContactsComponent implements OnInit, AfterViewInit {
    readonly ValidityStatus = ValidityStatus;
    readonly iso3ToEmojiFlag = iso3ToEmojiFlag;
    tableId = 'contacts'
    searchText = '';
    loading: boolean = false;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild('filtersPanel') filtersPanel!: MatExpansionPanel;

    allContacts = signal<Contact[]>([]);

    dataSource = new MatTableDataSource<Contact>();
    allColumns = [
        { key: 'valid', label: 'Valid', type: 'icon' },
        { key: 'firstName', label: 'First name', type: 'text' },
        { key: 'lastName', label: 'Last name', type: 'text' },
        { key: 'organisation', label: 'Organisation', type: 'chips' },
        { key: 'email', label: 'Email', type: 'text' },
        { key: 'topics', label: 'Topics of interest', type: 'chips' },
        { key: 'stakeholderGroups', label: 'Stakeholder groups', type: 'chips' },
        { key: 'projects', label: 'Projects', type: 'chips' },
        { key: 'initiatives', label: 'Initiatives', type: 'chips' },
        { key: 'events', label: 'Events', type: 'chips' },
        { key: 'contactTypes', label: 'Contact type', type: 'text' },
        { key: 'countries', label: 'Country', type: 'single-chip' },
        { key: 'lastModifiedOn', label: 'Last modified on', type: 'text' },
        // { key: 'optedOutOn', label: 'Opted out on', type: 'text' },
    ];
    columnLabelMap: Record<string, string> = {};

    defaultSelectedColumns: string[] = ['valid', 'firstName', 'lastName', 'organisation', 'topics', 'initiatives'];
    defaultColumnsOrder: string[] = this.allColumns.map(v => v.key);
    selectedColumns: string[] = this.defaultSelectedColumns;
    pageEvent: PageEvent | undefined;

    readonly showAllOrAny: Signal<boolean> = signal(false);

    readonly refDataService = inject(ReferenceDataService);
    readonly contactService = inject(ContactService);
    readonly tableStateService = inject(TableStateService);
    readonly initiativeService = inject(InitiativeService);
    readonly projectService = inject(ProjectService);
    readonly eventService = inject(EventService);
    readonly organisationService = inject(OrganisationService);
    readonly exportService = inject(ExportService);

    readonly allTopics = this.refDataService.getGeneralTopicsOfInterest();
    readonly allTopicOptions = computed(() => mapToNestedOptions(this.allTopics(), true));
    readonly allStakeholderGroups = this.refDataService.getContactStakeholdeGroups();
    readonly allStakeholderGroupOptions = computed(() => mapToNestedOptions(this.allStakeholderGroups(), true));
    readonly allProjects = toSignal(this.projectService.getAllProjects(), { initialValue: [] });
    readonly allProjectOptions = computed(() => mapToNestedOptions(this.allProjects(), true));
    readonly allInitiatives = toSignal(this.initiativeService.getAllInitiatives(), { initialValue: [] });
    readonly allInitiativeOptions = computed(() => mapToNestedOptions(this.allInitiatives(), true))
    readonly allEvents = toSignal(this.eventService.getAllEvents(), { initialValue: [] });
    readonly allEventOptions = computed(() => mapToNestedOptions(this.allEvents(), true));
    readonly allMemberContactTypes = Object.values(MemberContactType);
    readonly allMemberContactTypeOptions = [
        { value: MemberContactType.PRIMARY, label: 'Primary contact', isGroup: false },
        { value: MemberContactType.INVOICING, label: 'Invoicing contact', isGroup: false },
        { value: MemberContactType.CONTRACT, label: 'Contract contact', isGroup: false },
        { value: GovernanceType.BOD, label: 'Board of Directors', isGroup: false },
        { value: GovernanceType.SAB, label: 'Scientific Advisory Board', isGroup: false },
        { value: GovernanceType.GA, label: 'General Assembly', isGroup: false },
        { value: GovernanceType.EXECUTIVE, label: 'Executive Committee', isGroup: false },
        { value: GovernanceType.NOMINATION, label: 'Nomination Committee', isGroup: false },
        { value: GovernanceType.ETHICS, label: 'Ethics Committee', isGroup: false }
    ];

    readonly allCommunicationChoiceOptions = [
        {
            value: 'optIn', label: 'Accepts receiving', isGroup: true, children: [
                { value: '1', label: 'Event invitations', isGroup: false },
                { value: '2', label: 'Training opportunities', isGroup: false },
                { value: '3', label: 'Activity results', isGroup: false },
                { value: '4', label: 'Results from research studies', isGroup: false },
                { value: '5', label: 'Participation in feedback surveys', isGroup: false },
            ]
        },
        {
            value: 'optOut', label: 'Doesn\'t want to receive', isGroup: true, children: [
                { value: '6', label: 'Event invitations', isGroup: false },
                { value: '7', label: 'Training opportunities', isGroup: false },
                { value: '8', label: 'Activity results', isGroup: false },
                { value: '9', label: 'Results from research studies', isGroup: false },
                { value: '10', label: 'Participation in feedback surveys', isGroup: false },
            ]
        },
    ]

    readonly allOrganisationsIcons: AttributeIconPair<Organisation>[] = [
        {
            attributeName: 'archived',
            attributeIcon: 'archive'
        }
    ]
    readonly allOrganisations = toSignal(this.organisationService.getAllOrganisations(), { initialValue: [] });
    readonly allOrganisationOptions = computed(() => mapToNestedOptions(this.allOrganisations(), true, 'acronym', this.allOrganisationsIcons))
    readonly countries = this.refDataService.getCountries();
    readonly countryNames = computed(() => {
        const list = this.countries();
        return new Map(list.map(c => [c.isoCode, c.enName]));
    });
    readonly allCountryOptions = computed(() => this.countries().map(v =>
        ({ value: v.isoCode, label: iso3ToEmojiFlag(v.isoCode) + ' ' + v.enName, isGroup: false, flag: iso3ToEmojiFlag(v.isoCode) })));

    filterForm: FormGroup;
    filters: ContactFilters = this.getDefaultFilters();
    filtersExpanded: boolean = true;

    resetCounter = 0; // used to reset the filter form

    constructor(private readonly dialog: MatDialog, private readonly snackBar: MatSnackBar, private readonly router: Router,
        private readonly location: Location, private readonly cdr: ChangeDetectorRef, private readonly zone: NgZone,
        private readonly formBuilder: FormBuilder) {
        this.filterForm = {} as FormGroup;
    }

    ngOnInit(): void {
        this.columnLabelMap = Object.fromEntries(
            this.allColumns.map(col => [col.key, col.label])
        );
        this.loadContacts();

        // Display and clear any navigation message
        const state = this.location.getState() as { message?: string };
        if (state?.message) {
            this.snackBar.open(state.message, 'Close', {
                duration: 3000,
                panelClass: ['snackbar-success']
            });
            // Clear the message from history so it doesn't persist on reload
            history.replaceState({}, document.title, location.pathname);
        }

        this.filterForm = this.formBuilder.nonNullable.group<ContactFilters>({
            textFilter: '',
            topics: { selectedValues: [], matchAll: false },
            stakeholderGroups: { selectedValues: [], matchAll: false },
            projects: { selectedValues: [], matchAll: false },
            initiatives: { selectedValues: [], matchAll: false },
            events: { selectedValues: [], matchAll: false },
            organisations: { selectedValues: [], matchAll: false },
            contactTypes: { selectedValues: [], matchAll: false },
            countries: { selectedValues: [], matchAll: false },
            governance: { selectedValues: [], matchAll: false },
            communicationChoices: { selectedValues: [], matchAll: false },
            showActiveItems: true,
            showArchivedItems: false
        });
        // this.filterForm.get('communicationChoices')?.disable();
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        // this.dataSource.filterPredicate = this.filterFunction;
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'valid':
                    return item.validityStatus();
                case 'firstName':
                    return item.firstName.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
                case 'lastName':
                    return item.lastName.toLowerCase() ?? 'N/A'; // Ensure case-insensitive sorting
                case 'email':
                    return item.email1?.toLowerCase() ?? 'N/A';
                case 'lastModifiedOn':
                    return item.lastModifiedOn ?? 'N/A';
                default:
                    return (item as Record<string, any>)[property];
            }
        }

        const saved = this.tableStateService.getState<ContactFilters>(this.tableId);
        if (saved) {
            // console.debug('restoring', saved)
            this.zone.onStable.pipe(take(1)).subscribe(() => {
                this.paginator.pageIndex = saved.pageIndex;
                this.paginator.pageSize = saved.pageSize;
                this.sort.active = saved.sortActive;
                this.sort.direction = saved.sortDirection;
                this.filterForm.patchValue(saved.filters ? saved.filters : this.getDefaultFilters());
                this.filtersExpanded = saved.filtersExpanded;
                this.selectedColumns = saved.selectedColumnKeys ?? this.defaultSelectedColumns;
                if (saved.allColumnKeys) {
                    this.allColumns.sort(
                        (a, b) => saved.allColumnKeys.indexOf(a.key) - saved.allColumnKeys.indexOf(b.key)
                    );
                }
                this.cdr.detectChanges();
            });
        }
        merge(
            this.filtersPanel.expandedChange,
            this.paginator.page,
            this.sort.sortChange,
            this.filterForm.valueChanges.pipe(debounceTime(300))
        ).subscribe(() => {
            this.saveState();
        });

        this.filterForm.valueChanges
            .pipe(debounceTime(300))
            .subscribe(filters => {
                // console.debug('valueChanges', this.filterForm.getRawValue());
                const filtered = this.allContacts().filter(contact =>
                    this.filterFunction(contact, JSON.stringify(this.filterForm.getRawValue()))
                );
                this.dataSource.data = filtered;
            });
    }

    // get displayedColumns(): string[] {
    //   return this.selectedColumns;
    // }

    loadContacts(): void {
        this.loading = true;

        this.contactService.getAllContacts().subscribe({
            next: contacts => {

                this.allContacts.set(contacts);
                const filtered = this.allContacts().filter(contact =>
                    this.filterFunction(contact, JSON.stringify(this.filterForm.getRawValue()))
                );
                this.dataSource.data = filtered;

                this.loading = false;
            },
            error: msg => {
                console.error('Failed to load contacts:', msg);
                this.snackBar.open('Failed to load contacts', 'Close', {
                    duration: 3000,
                    panelClass: ['snackbar-error']
                });
                this.loading = false;
            }
        });
    }

    saveState() {
        const state = {
            pageIndex: this.paginator?.pageIndex ?? 0,
            pageSize: this.paginator?.pageSize ?? 10,
            sortActive: this.sort?.active,
            sortDirection: this.sort?.direction,
            filters: this.filterForm.getRawValue(),
            filtersExpanded: this.filtersExpanded,
            allColumnKeys: this.allColumns.map(c => (c.key)),
            selectedColumnKeys: this.selectedColumns,
        }
        // console.debug('Saving state', state);
        this.tableStateService.setState(this.tableId, state);
    }

    goToDetail(contact: Contact): void {
        this.saveState();
        this.router.navigate(['/contacts', contact.id]).then(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });

    }

    add(): void {
        this.saveState();
        this.router.navigate(['/contacts', 'new']).then(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }

    delete(id: number): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Delete Event',
                message: 'Are you sure you want to delete this contact?'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.contactService.delete(id).subscribe({
                    next: () => {
                        this.loadContacts();
                        this.snackBar.open('Contact deleted', 'Close', { duration: 3000 });
                    },
                    error: msg => {
                        this.snackBar.open('Failed to delete contact', 'Close', { duration: 3000 });
                        console.error('Delete failed:', msg)
                    }
                });
            }
        });
    }

    export(): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Export list',
                message: 'The information exported will correspond to the current selection of visible columns and search filters',
                buttonText: {
                    ok: "Proceed",
                    cancel: "Cancel"
                }
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                const data: any[] = [];
                this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort!).forEach(contact => {
                    const enriched = {
                        ...contact,
                        valid: contact.validityStatus(),
                        archived: contact.archived,
                        countries: contact.countryCodes.map(c => this.countryNames().get(c)).join(', '),
                        contactTypes: contact.contactTypes.join(', '),
                        email: contact.emails.join(', '),
                        organisation: contact.organisations.join(', '),
                    };
                    const row = mapObject(enriched, [...this.selectedColumns, 'archived']);
                    data.push(row);
                });
                const headers = Object.keys(data[0]);
                this.exportService.generateReport('contacts', { headers: headers, data: data }).subscribe({
                    next: (report) => {
                        console.debug('OK');
                        const ts = DateTime.now().toFormat("yyyyMMdd-HHmmss");
                        let filename = `nexus_contacts_${ts}.xlsx`;
                        const url = window.URL.createObjectURL(report);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        a.click();
                        window.URL.revokeObjectURL(url);
                    },
                    error: (error) => {
                        console.error('Failed to generate report', error);
                        this.snackBar.open('Failed to generate report', 'Close', { duration: 3000 });
                    }
                })
            }
        })
    }

    openColumnSelector(): void {
        const dialogRef = this.dialog.open(ColumnSelectorDialogComponent, {
            data: {
                allColumns: this.allColumns,
                selected: [...this.selectedColumns],
                defaultSelection: [...this.defaultSelectedColumns],
                defaultOrder: [...this.defaultColumnsOrder]
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.selectedColumns = result.selected;
                this.allColumns = result.allColumns;
                this.saveState();
            }
        });
    }

    getDefaultFilters(): ContactFilters {
        return {
            textFilter: '',
            topics: createAdvancedSelectValue(),
            stakeholderGroups: createAdvancedSelectValue(),
            projects: createAdvancedSelectValue(),
            initiatives: createAdvancedSelectValue(),
            events: createAdvancedSelectValue(),
            organisations: createAdvancedSelectValue(),
            contactTypes: createAdvancedSelectValue(),
            countries: createAdvancedSelectValue(),
            governance: createAdvancedSelectValue(),
            communicationChoices: { selectedValues: [], matchAll: false },
            showActiveItems: true,
            showArchivedItems: false
        };
    }

    clearAllFilters() {
        console.debug('clear all filters')
        this.resetCounter++; // increment → triggers ngOnChanges in children
        this.filterForm.reset();
    }

    toggleFilterMatch(fieldName: keyof ContactFilters) {
        this.filterForm.get(fieldName)?.setValue(!this.filterForm.get(fieldName)?.value);
    }

    togglePanel(event: MouseEvent) {
        event.stopPropagation(); // prevent header click from toggling
        this.filtersExpanded = !this.filtersExpanded;
    }

    // toggleShowActiveItems() {
    //     console.debug('setting showActiveItems to ', !this.showActiveItems())
    //     this.showActiveItems.set(!this.showActiveItems())
    //     this.dataSource.filter = this.dataSource.filter;
    // }

    // toggleShowArchivedItems() {
    //     console.debug('setting showArchivedItems to ', !this.showArchivedItems())
    //     this.showArchivedItems.set(!this.showArchivedItems())
    //     this.dataSource.filter = this.dataSource.filter;
    // }

    /**
     * creates the template for the table columns
     */
    get gridTemplate() {
        return getGridTemplate(this.selectedColumns, this.allColumns);
    }

    /**
     * Called whenever a value selection changes in one of the filters
     * @param fieldName
     * @param selectedValues
     */
    onSelectionChange(fieldName: keyof ContactFilters, event: { selectedValues: string[], matchAll: boolean }) {
        let control = this.filterForm.get(fieldName);
        control?.setValue(event.selectedValues, { emitEvent: true });
        const matchAllControlName = fieldName.replace(/Filter$/, "MatchAll");
        control = this.filterForm.get(matchAllControlName);
        control?.setValue(event.matchAll, { emitEvent: true });
    }

    /**
     *
     * @param contact
     * @param filter
     * @returns
     */

    // I have to defined it this way so that the this context is preserved

    filterFunction = (contact: Contact, filter: string) => {
        const rawFilters = JSON.parse(filter);
        const filters: ContactFilters = {
            ...rawFilters,
        };
        if (!filters.showActiveItems && !contact.archived) {
            return false;
        }

        if (!filters.showArchivedItems && contact.archived) {
            return false;
        }

        if (filters?.textFilter) {
            const searchStr = filters.textFilter.toLowerCase();
            const inFirstName = contact.firstName?.toLowerCase().includes(searchStr);
            const inLastName = contact.lastName?.toLowerCase().includes(searchStr);
            const inEmail = contact.email1?.toLowerCase().includes(searchStr) || contact.email2?.toLowerCase().includes(searchStr);
            if (!inFirstName && !inLastName && !inEmail) {
                return false;
            }
        }

        if (filters?.communicationChoices.selectedValues && filters.communicationChoices.selectedValues.length > 0) {
            // Ugly hack, but communication options are hardcoded
            let selectedValues = filters.communicationChoices.selectedValues;
            // Opt In
            if ((selectedValues.includes("1") && (!contact.communicationType1OptIn)) ||
                (selectedValues.includes("2") && (!contact.communicationType2OptIn)) ||
                (selectedValues.includes("3") && (!contact.communicationType3OptIn)) ||
                (selectedValues.includes("4") && (!contact.communicationType4OptIn)) ||
                (selectedValues.includes("5") && (!contact.communicationType5OptIn))) {
                return false;
            }
            // Opt Out
            if ((selectedValues.includes("6") && (contact.communicationType1OptIn)) ||
                (selectedValues.includes("7") && (contact.communicationType2OptIn)) ||
                (selectedValues.includes("8") && (contact.communicationType3OptIn)) ||
                (selectedValues.includes("9") && (contact.communicationType4OptIn)) ||
                (selectedValues.includes("10") && (contact.communicationType5OptIn))) {
                return false;
            }
        }

        if (filters?.topics.selectedValues && filters.topics.selectedValues.length > 0) {
            const intersection = contact.topics.filter(item => filters.topics.selectedValues.includes(String(item.id)));
            if (filters.topics.matchAll) {
                if (intersection.length < filters.topics.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }

        if (filters?.stakeholderGroups.selectedValues && filters.stakeholderGroups.selectedValues.length > 0) {
            const intersection = contact.stakeholderGroups.filter(item => filters.stakeholderGroups.selectedValues.includes(String(item.id)));
            if (filters.stakeholderGroups.matchAll) {
                if (intersection.length < filters.stakeholderGroups.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }

        if (filters?.projects.selectedValues && filters.projects.selectedValues.length > 0) {
            const intersection = contact.projects.filter(item => filters.projects.selectedValues.includes(String(item.id)));
            if (filters.projects.matchAll) {
                if (intersection.length < filters.projects.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }

        if (filters?.initiatives.selectedValues && filters.initiatives.selectedValues.length > 0) {
            const intersection = contact.initiatives.filter(item => filters.initiatives.selectedValues.includes(String(item.id)));
            if (filters.initiatives.matchAll) {
                if (intersection.length < filters.initiatives.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }

        if (filters?.events.selectedValues && filters.events.selectedValues.length > 0) {
            const intersection = contact.events.filter(item => filters.events.selectedValues.includes(String(item.id)));
            if (filters.events.matchAll) {
                if (intersection.length < filters.events.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }

        if (filters?.organisations.selectedValues && filters.organisations.selectedValues.length > 0) {
            const affiliations = contact.affiliations ?? [];
            const intersection = affiliations.filter(
                item => item?.organisation?.id && filters.organisations.selectedValues.includes(String(item.organisation.id)));
            if (filters.organisations.matchAll) {
                if (intersection.length < filters.organisations.selectedValues.length) {
                    return false
                }
            } else if (intersection.length === 0) {
                return false;
            }
        }

        if (filters?.contactTypes.selectedValues && filters.contactTypes.selectedValues.length > 0) {
            if (!contact.affiliations) {
                return false;
            }
            const intersection = contact.affiliations.filter(item =>
                (filters.contactTypes.selectedValues.includes(MemberContactType.PRIMARY) && item.activePrimaryContact) ||
                (filters.contactTypes.selectedValues.includes(MemberContactType.INVOICING) && item.activeInvoicingContact) ||
                (filters.contactTypes.selectedValues.includes(MemberContactType.CONTRACT) && item.activeContractContact)
            );

            const selectedGovernanceTypes = filters.contactTypes.selectedValues
                .filter(v => v in governanceChecks) as GovernanceType[];


            const governanceMatchCount = selectedGovernanceTypes.filter(type => governanceChecks[type](contact)).length;


            if (filters.contactTypes.matchAll) {
                if ((intersection.length + governanceMatchCount) < filters.contactTypes.selectedValues.length) {
                    return false
                }
            } else if ((intersection.length + governanceMatchCount) === 0) {
                return false;
            }
        }

        if (filters?.countries.selectedValues && filters.countries.selectedValues.length > 0) {
            const contactCountry = contact.postalAddress?.countryIsoCode ?? null;
            const orgCountries = [
                ...new Set(
                    (contact.affiliations ?? [])
                        .flatMap(item => item.organisation?.countries || [])
                        .filter(code => code != null && code !== "")
                )
            ];
            const allCountries = new Set([
                ...(contactCountry ? [contactCountry] : []),
                ...orgCountries
            ]);

            if (!filters.countries.selectedValues.some(code => allCountries.has(code))) {
                return false;
            }
        }

        return true;
    }


    hasPrimary(contact: Contact): boolean {
        return contact.affiliations?.some(a => a.activePrimaryContact) ?? false;
    }

    hasInvoicing(contact: Contact): boolean {
        return contact.affiliations?.some(a => a.activeInvoicingContact) ?? false;
    }

    hasContract(contact: Contact): boolean {
        return contact.affiliations?.some(a => a.activeContractContact) ?? false;
    }

    getAllCountries(contact: Contact): string[] {
        // Per Planner task 20260915: country associated with a contact 
        // should override the country of any affiliated organisation in the search field
        const contactCountry = contact.postalAddress?.countryIsoCode ?? null;
        if (contactCountry) {
            return [contactCountry];
        }
        const orgCountries = [
            ...new Set(
                (contact.affiliations ?? [])
                    .flatMap(item => item.organisation?.countries || [])
                    .filter(code => code != null && code !== "")
            )
        ];
        const allCountries = new Set([
            ...(contactCountry ? [contactCountry] : []),
            ...orgCountries
        ]);
        return Array.from(allCountries);
    }
}
